import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { Checkbox } from '../../../components/ui/Checkbox';

const SecuritySettings = () => {
  const [passwordPolicy, setPasswordPolicy] = useState({
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    expirationDays: 90,
    preventReuse: 5,
    maxAttempts: 5,
    lockoutDuration: 30
  });

  const [sessionSettings, setSessionSettings] = useState({
    timeoutMinutes: 60,
    maxConcurrentSessions: 3,
    persistSession: true,
    forceLogoutInactive: true
  });

  const [twoFactorAuth, setTwoFactorAuth] = useState({
    enabled: false,
    required: false,
    methods: ['email', 'sms']
  });

  const [hasChanges, setHasChanges] = useState(false);

  const handlePasswordPolicyChange = (field, value) => {
    setPasswordPolicy(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSessionChange = (field, value) => {
    setSessionSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleTwoFactorChange = (field, value) => {
    setTwoFactorAuth(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Mock save functionality
    console.log('Saving security settings:', { passwordPolicy, sessionSettings, twoFactorAuth });
    setHasChanges(false);
  };

  const handleReset = () => {
    // Reset to default values
    setPasswordPolicy({
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
      expirationDays: 90,
      preventReuse: 5,
      maxAttempts: 5,
      lockoutDuration: 30
    });
    setSessionSettings({
      timeoutMinutes: 60,
      maxConcurrentSessions: 3,
      persistSession: true,
      forceLogoutInactive: true
    });
    setTwoFactorAuth({
      enabled: false,
      required: false,
      methods: ['email', 'sms']
    });
    setHasChanges(false);
  };

  const generatePasswordExample = () => {
    const requirements = [];
    if (passwordPolicy?.requireUppercase) requirements?.push('A-Z');
    if (passwordPolicy?.requireLowercase) requirements?.push('a-z');
    if (passwordPolicy?.requireNumbers) requirements?.push('0-9');
    if (passwordPolicy?.requireSpecialChars) requirements?.push('!@#$');
    
    return `Example: SecurePass123! (${passwordPolicy?.minLength}+ chars, ${requirements?.join(', ')})`;
  };

  return (
    <div className="space-y-8">
      {/* Password Policy Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Password Policy</h3>
            <p className="text-sm text-muted-foreground">Configure password requirements and security rules</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              label="Minimum Length"
              type="number"
              value={passwordPolicy?.minLength}
              onChange={(e) => handlePasswordPolicyChange('minLength', parseInt(e?.target?.value))}
              min={6}
              max={32}
              description="Minimum number of characters required"
            />

            <Input
              label="Expiration (Days)"
              type="number"
              value={passwordPolicy?.expirationDays}
              onChange={(e) => handlePasswordPolicyChange('expirationDays', parseInt(e?.target?.value))}
              min={30}
              max={365}
              description="Password expires after this many days"
            />

            <Input
              label="Prevent Reuse (Last N passwords)"
              type="number"
              value={passwordPolicy?.preventReuse}
              onChange={(e) => handlePasswordPolicyChange('preventReuse', parseInt(e?.target?.value))}
              min={0}
              max={20}
              description="Number of previous passwords to remember"
            />
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">Character Requirements</label>
              <div className="space-y-3">
                <Checkbox
                  label="Require uppercase letters (A-Z)"
                  checked={passwordPolicy?.requireUppercase}
                  onChange={(e) => handlePasswordPolicyChange('requireUppercase', e?.target?.checked)}
                />
                <Checkbox
                  label="Require lowercase letters (a-z)"
                  checked={passwordPolicy?.requireLowercase}
                  onChange={(e) => handlePasswordPolicyChange('requireLowercase', e?.target?.checked)}
                />
                <Checkbox
                  label="Require numbers (0-9)"
                  checked={passwordPolicy?.requireNumbers}
                  onChange={(e) => handlePasswordPolicyChange('requireNumbers', e?.target?.checked)}
                />
                <Checkbox
                  label="Require special characters (!@#$)"
                  checked={passwordPolicy?.requireSpecialChars}
                  onChange={(e) => handlePasswordPolicyChange('requireSpecialChars', e?.target?.checked)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-2">Password Preview</h4>
          <p className="text-sm text-muted-foreground">{generatePasswordExample()}</p>
        </div>
      </div>
      {/* Account Lockout Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Lock" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Account Lockout Policy</h3>
            <p className="text-sm text-muted-foreground">Configure failed login attempt handling</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Maximum Failed Attempts"
            type="number"
            value={passwordPolicy?.maxAttempts}
            onChange={(e) => handlePasswordPolicyChange('maxAttempts', parseInt(e?.target?.value))}
            min={3}
            max={10}
            description="Account locks after this many failed attempts"
          />

          <Input
            label="Lockout Duration (Minutes)"
            type="number"
            value={passwordPolicy?.lockoutDuration}
            onChange={(e) => handlePasswordPolicyChange('lockoutDuration', parseInt(e?.target?.value))}
            min={5}
            max={1440}
            description="How long account remains locked"
          />
        </div>
      </div>
      {/* Session Management Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Clock" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Session Management</h3>
            <p className="text-sm text-muted-foreground">Configure user session behavior and limits</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              label="Session Timeout (Minutes)"
              type="number"
              value={sessionSettings?.timeoutMinutes}
              onChange={(e) => handleSessionChange('timeoutMinutes', parseInt(e?.target?.value))}
              min={15}
              max={480}
              description="Automatic logout after inactivity"
            />

            <Input
              label="Max Concurrent Sessions"
              type="number"
              value={sessionSettings?.maxConcurrentSessions}
              onChange={(e) => handleSessionChange('maxConcurrentSessions', parseInt(e?.target?.value))}
              min={1}
              max={10}
              description="Maximum simultaneous logins per user"
            />
          </div>

          <div className="space-y-4">
            <Checkbox
              label="Persist sessions across browser restarts"
              description="Users stay logged in when closing/reopening browser"
              checked={sessionSettings?.persistSession}
              onChange={(e) => handleSessionChange('persistSession', e?.target?.checked)}
            />

            <Checkbox
              label="Force logout inactive users"
              description="Automatically log out users after timeout period"
              checked={sessionSettings?.forceLogoutInactive}
              onChange={(e) => handleSessionChange('forceLogoutInactive', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Two-Factor Authentication Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Smartphone" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h3>
            <p className="text-sm text-muted-foreground">Enhanced security with multi-factor authentication</p>
          </div>
        </div>

        <div className="space-y-4">
          <Checkbox
            label="Enable Two-Factor Authentication"
            description="Allow users to set up 2FA for their accounts"
            checked={twoFactorAuth?.enabled}
            onChange={(e) => handleTwoFactorChange('enabled', e?.target?.checked)}
          />

          {twoFactorAuth?.enabled && (
            <div className="ml-6 space-y-4">
              <Checkbox
                label="Require 2FA for all users"
                description="Make two-factor authentication mandatory for all accounts"
                checked={twoFactorAuth?.required}
                onChange={(e) => handleTwoFactorChange('required', e?.target?.checked)}
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">Available Methods</label>
                <div className="space-y-2">
                  <Checkbox
                    label="Email verification codes"
                    checked={twoFactorAuth?.methods?.includes('email')}
                    onChange={(e) => {
                      const methods = e?.target?.checked 
                        ? [...twoFactorAuth?.methods, 'email']
                        : twoFactorAuth?.methods?.filter(m => m !== 'email');
                      handleTwoFactorChange('methods', methods);
                    }}
                  />
                  <Checkbox
                    label="SMS verification codes"
                    checked={twoFactorAuth?.methods?.includes('sms')}
                    onChange={(e) => {
                      const methods = e?.target?.checked 
                        ? [...twoFactorAuth?.methods, 'sms']
                        : twoFactorAuth?.methods?.filter(m => m !== 'sms');
                      handleTwoFactorChange('methods', methods);
                    }}
                  />
                  <Checkbox
                    label="Authenticator apps (TOTP)"
                    checked={twoFactorAuth?.methods?.includes('totp')}
                    onChange={(e) => {
                      const methods = e?.target?.checked 
                        ? [...twoFactorAuth?.methods, 'totp']
                        : twoFactorAuth?.methods?.filter(m => m !== 'totp');
                      handleTwoFactorChange('methods', methods);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex items-center justify-between p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <span className="text-sm font-medium text-warning">You have unsaved changes</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset}>
              Reset Changes
            </Button>
            <Button variant="default" onClick={handleSave}>
              Save Security Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;